import type { EdgeConfigurationDraft } from "@sitescene/shared";

import { sourceLabels } from "./editor";

const labels: Record<string, string> = {
	audio_enabled: "Include audio",
	ca_bundle_pem: "CA certificates",
	ha_entity_id: "Home Assistant entity ID",
	host: "Host",
	integration_id: "Shared integration",
	integration_type: "Integration type",
	name: "Name",
	onb_host: "Video bridge host",
	onb_port: "Video bridge port",
	onb_username: "Video bridge username",
	port: "Port",
	segment_duration: "Segment duration (seconds)",
	segment_format: "Format",
	sensor_event_topic: "Event topic",
	sensor_host: "Host",
	sensor_id: "Sensor ID",
	sensor_port: "Port",
	sensor_username: "Username",
	skip_upload: "Local only",
	source_type: "Source type",
	tls_verify: "Verify TLS",
	username: "Username",
	video_bitrate: "Bitrate (kbps)",
	video_fps: "Frames per second",
	video_passthrough: "Video passthrough",
	video_resolution: "Resolution",
	video_sensor_id: "Video Sensor ID",
	zone_id: "Zone ID",
};
/** Immutable authoring inspection, never a download credential or a device operation. */
export function RevisionContents({
	draft,
	sourceUrls,
	unreadableSourceUrls,
}: {
	draft: EdgeConfigurationDraft;
	sourceUrls: Record<string, string>;
	unreadableSourceUrls: string[];
}) {
	return (
		<div className="space-y-3">
			{!draft.sources.length && <p>No sources in this revision.</p>}
			{[...draft.sources, ...draft.integrations].map((item) => (
				<details
					className="rounded-md border border-secondary p-3"
					key={item.id}
				>
					<summary className="cursor-pointer font-medium">
						{item.fields.name}
						{"source_type" in item.fields
							? ` · ${sourceLabels[item.fields.source_type]}`
							: " · Shared integration"}
					</summary>
					<dl className="mt-3 grid min-w-0 gap-2 text-sm sm:grid-cols-[12rem_minmax(0,1fr)]">
						{Object.entries(item.fields)
							.filter(
								([key, value]) =>
									value != null &&
									value !== "" &&
									!["name", "source_type"].includes(key),
							)
							.map(([key, value]) => (
								<div className="contents" key={key}>
									<dt className="text-secondary">{labels[key] ?? key}</dt>
									<dd className="break-all whitespace-pre-wrap">
										{typeof value === "boolean"
											? value
												? "Yes"
												: "No"
											: String(value)}
									</dd>
								</div>
							))}
						{"desiredState" in item && (
							<>
								<dt>Operation</dt>
								<dd>{item.desiredState}</dd>
							</>
						)}
						{sourceUrls[item.id] && (
							<>
								<dt>RTSP stream URL</dt>
								<dd className="font-mono break-all">{sourceUrls[item.id]}</dd>
							</>
						)}
						{unreadableSourceUrls.includes(item.id) && (
							<>
								<dt>RTSP stream URL</dt>
								<dd>Stored, but it can't be read with the current key.</dd>
							</>
						)}
						{!!Object.keys(item.secrets).filter((key) => key !== "rtsp_url")
							.length && (
							<>
								<dt>Credentials</dt>
								<dd>Stored securely; values are not displayed.</dd>
							</>
						)}
					</dl>
				</details>
			))}
		</div>
	);
}
